import React, { Fragment, useState, useEffect } from "react";
import Navbar from "../../../Components/Navbar";
import SideBar from "../../../Components/Sidebar";
import style from "./../index.module.scss";
import LevelTwoHeader from "../../../Components/LevelTwoHeader";
import { GET, PUT } from "../../dataSaver";
import { formatInTimeZone } from "date-fns-tz";
import { siteTimeZone, timeZoneAbbreviation } from "../../../utils/formatting";

const EMAIL_FREQUENCY_OPTIONS = [
  { value: "DAILY", label: "Daily" },
  { value: "WEEKLY", label: "Weekly" },
];

const APPLICATION_VALIDITY_OPTIONS = [
  { value: "NORMAL_CALENDAR_YEAR", label: "Normal Calendar Year" },
  { value: "APPLICATION_CALENDAR_YEAR", label: "Application Calendar Year" },
];

const DEFAULT_SETTINGS = {
  applicationValidity: "NORMAL_CALENDAR_YEAR",
  medicalDirectivePendingAttestationEmailFrequency: "WEEKLY",
  medicalDirectiveAttestationAcknowledgementEmailFrequency: "WEEKLY",
};

const normalizeFrequency = (value, fallback = "WEEKLY") => {
  const normalized = String(value || "")
    .trim()
    .toUpperCase();
  return EMAIL_FREQUENCY_OPTIONS.some((option) => option.value === normalized)
    ? normalized
    : fallback;
};

const SettingList = () => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [entityId, setEntityId] = useState("");
  const [lastUpdatedDate, setLastUpdatedDate] = useState("");
  const [applicationValidity, setApplicationValidity] = useState(
    DEFAULT_SETTINGS.applicationValidity
  );
  const [
    medicalDirectivePendingAttestationEmailFrequency,
    setMedicalDirectivePendingAttestationEmailFrequency,
  ] = useState(
    DEFAULT_SETTINGS.medicalDirectivePendingAttestationEmailFrequency
  );
  const [
    medicalDirectiveAttestationAcknowledgementEmailFrequency,
    setMedicalDirectiveAttestationAcknowledgementEmailFrequency,
  ] = useState(
    DEFAULT_SETTINGS.medicalDirectiveAttestationAcknowledgementEmailFrequency
  );
  const [responseMessage, setResponseMessage] = useState("");
  const [responseIsError, setResponseIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    getEntity();
    loadEntitySettings();
  }, []);

  useEffect(() => {
    if (entityId) {
      getLastModifiedDate();
    }
  }, [entityId]);

  const getIsExpanded = (value) => {
    setIsExpanded(value);
  };

  const showMessage = (message, isError = false) => {
    setResponseIsError(isError);
    setResponseMessage(message);
    setTimeout(() => {
      setResponseMessage("");
      setResponseIsError(false);
    }, 2000);
  };

  const applySettings = (data = {}) => {
    setApplicationValidity(
      data?.applicationValidity || DEFAULT_SETTINGS.applicationValidity
    );
    setMedicalDirectivePendingAttestationEmailFrequency(
      normalizeFrequency(
        data?.medicalDirectivePendingAttestationEmailFrequency,
        DEFAULT_SETTINGS.medicalDirectivePendingAttestationEmailFrequency
      )
    );
    setMedicalDirectiveAttestationAcknowledgementEmailFrequency(
      normalizeFrequency(
        data?.medicalDirectiveAttestationAcknowledgementEmailFrequency,
        DEFAULT_SETTINGS.medicalDirectiveAttestationAcknowledgementEmailFrequency
      )
    );
  };

  const loadEntitySettings = async () => {
    setIsLoading(true);
    try {
      const { data } = await GET("entity-service/entitySetting");
      applySettings(data || {});
    } catch (error) {
      console.error("Error loading entity settings:", error);
      showMessage("Failed to load settings.", true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!applicationValidity) {
      showMessage("Please select a valid option for application validity.", true);
      return;
    }

    setIsSubmitting(true);
    try {
      await PUT("entity-service/entitySetting", {
        applicationValidity,
        medicalDirectivePendingAttestationEmailFrequency,
        medicalDirectiveAttestationAcknowledgementEmailFrequency,
      });
      showMessage("Setting saved successfully!");
    } catch (error) {
      console.error("Error saving setting:", error);
      showMessage(
        error?.response?.data?.message || "Failed to save setting.",
        true
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const getEntity = async () => {
    try {
      const { data: entity } = await GET(`entity-service/entity`);
      setEntityId(entity?.[0]?.id);
    } catch (error) {
      console.error("Error loading entity:", error);
    }
  };

  const getLastModifiedDate = async () => {
    try {
      const { data: lastModifiedDate } = await GET(
        `entity-service/referenceList/entity/${entityId}`
      );
      const date = new Date(lastModifiedDate.departments?.lastModified);
      if (Number.isNaN(date.getTime())) {
        setLastUpdatedDate("");
        return;
      }
      setLastUpdatedDate(
        `${formatInTimeZone(date, siteTimeZone(), "MMM d, yyyy HH:mm")} ${timeZoneAbbreviation()}`
      );
    } catch (error) {
      console.error("Error loading last modified date:", error);
    }
  };

  return (
    <Fragment>
      <Navbar />
      <div className={style.margin20}>
        <div className={`${isExpanded ? style.bigCardGrid : style.smallCardGrid}`}>
          <div>
            <SideBar isExpanded={isExpanded} getIsExpanded={getIsExpanded}>
              <div></div>
            </SideBar>
          </div>
          <div>
            <LevelTwoHeader
              heading="Setting List"
              updatedTime={
                lastUpdatedDate ? `UPDATED ON ${lastUpdatedDate}` : ""
              }
              path="/Screens/ReferenceList/customerAdminDashboard"
              callingFrom="Customer Admin"
              needHeader={true}
              tileType="Applicant"
            />
            <div className={style.marginTop55}>
              <div className={style.centreCardStyle}>
                <div className={style.margin20}>
                  {isLoading ? (
                    <div className={style.responseMessage}>Loading settings…</div>
                  ) : (
                    <div className={style.fieldContainer}>
                      <label
                        htmlFor="applicationValidity"
                        className={style.entityLabelStyle}
                      >
                        Application Validity
                      </label>
                      <select
                        id="applicationValidity"
                        value={applicationValidity}
                        onChange={(e) => setApplicationValidity(e.target.value)}
                        className={style.inputField}
                      >
                        {APPLICATION_VALIDITY_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>

                      <label
                        htmlFor="medicalDirectivePendingAttestationEmailFrequency"
                        className={`${style.entityLabelStyle} ${style.marginTop20}`}
                      >
                        Medical Directive Pending Attestation Email Frequency
                      </label>
                      <select
                        id="medicalDirectivePendingAttestationEmailFrequency"
                        value={medicalDirectivePendingAttestationEmailFrequency}
                        onChange={(e) =>
                          setMedicalDirectivePendingAttestationEmailFrequency(
                            e.target.value
                          )
                        }
                        className={style.inputField}
                      >
                        {EMAIL_FREQUENCY_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>

                      <label
                        htmlFor="medicalDirectiveAttestationAcknowledgementEmailFrequency"
                        className={`${style.entityLabelStyle} ${style.marginTop20}`}
                      >
                        Medical Directive Attestation Acknowledgement Email
                        Frequency
                      </label>
                      <select
                        id="medicalDirectiveAttestationAcknowledgementEmailFrequency"
                        value={
                          medicalDirectiveAttestationAcknowledgementEmailFrequency
                        }
                        onChange={(e) =>
                          setMedicalDirectiveAttestationAcknowledgementEmailFrequency(
                            e.target.value
                          )
                        }
                        className={style.inputField}
                      >
                        {EMAIL_FREQUENCY_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>

                      <div className={style.actionButtonContainer}>
                        <button
                          onClick={handleSubmit}
                          className={style.submitButton}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? "Submitting..." : "Save Setting"}
                        </button>
                      </div>
                      {responseMessage ? (
                        <div
                          className={`${style.responseMessage} ${
                            responseIsError ? style.responseMessageError : ""
                          }`}
                        >
                          {responseMessage}
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default SettingList;
